// src/pages/ServiceProviderProfile.tsx: full public provider profile
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { supabase } from '../services/supabase';
import {
  C, catColor, hqLabel, signedAssetUrl,
  ServiceProvider, ServiceProduct, ServiceConnection, ServiceMessage,
} from '../lib/serviceConnect';

const card: React.CSSProperties = {
  background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};
const labelStyle: React.CSSProperties = {
  fontSize: '10px', fontWeight: 700, color: C.muted,
  letterSpacing: '0.6px', textTransform: 'uppercase', display: 'block', marginBottom: '5px',
};

type Tab = 'overview' | 'products' | 'connect';

export default function ServiceProviderProfile() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, firm } = useProfile();

  const [provider, setProvider] = useState<ServiceProvider | null>(null);
  const [products, setProducts] = useState<ServiceProduct[]>([]);
  const [logoUrl, setLogoUrl]   = useState<string | null>(null);
  const [deckUrl, setDeckUrl]   = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [conn, setConn]         = useState<ServiceConnection | null>(null);
  const [loading, setLoading]   = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab]           = useState<Tab>('overview');

  const isOwner = !!(provider && user && provider.owner_id === user.id);

  useEffect(() => { if (id) load(id); /* eslint-disable-next-line */ }, [id, user]);

  async function load(pid: string) {
    setLoading(true);
    const { data } = await supabase.from('service_providers').select('*').eq('id', pid).maybeSingle();
    if (!data) { setNotFound(true); setLoading(false); return; }
    const p = data as ServiceProvider;
    setProvider(p);

    const { data: prods } = await supabase
      .from('service_provider_products').select('*').eq('provider_id', pid)
      .order('is_flagship', { ascending: false }).order('created_at', { ascending: true });
    setProducts((prods as ServiceProduct[]) || []);

    // signedAssetUrl passes full URLs (https://…) through untouched and signs
    // bare storage paths. Assigning unconditionally clears stale assets when
    // switching between providers.
    setLogoUrl(await signedAssetUrl(p.logo_url));
    setDeckUrl(await signedAssetUrl(p.pitch_deck_url, 7200));
    setVideoUrl(await signedAssetUrl(p.demo_video_url, 7200));

    if (user) {
      // limit(1): a firm can accumulate more than one request row, and a bare
      // maybeSingle() throws on multiple matches.
      const { data: c } = await supabase.from('service_connections')
        .select('*').eq('provider_id', pid).eq('requested_by', user.id)
        .order('requested_at', { ascending: false }).limit(1).maybeSingle();
      setConn((c as ServiceConnection) || null);
    } else {
      setConn(null);
    }
    setLoading(false);
  }

  if (loading) return (
    <div style={{ display: 'flex', height: '60vh', alignItems: 'center', justifyContent: 'center', background: C.surface }}>
      <div style={{ color: C.muted, fontSize: '13px', fontFamily: 'system-ui' }}>Loading profile…</div>
    </div>
  );

  // Providers are bounced out of the marketplace, so send them back to their
  // own dashboard rather than through a redirect.
  const isProviderAccount = profile?.role === 'service_provider';
  const backTo = isProviderAccount ? '/service-provider-dashboard' : '/service-connect';
  const backLabel = isProviderAccount ? '← Back to my dashboard' : '← Back to ServiceConnect';

  if (notFound || !provider) return (
    <div style={{ padding: '48px 32px', background: C.surface, minHeight: '100vh', fontFamily: 'system-ui' }}>
      <button onClick={() => navigate(backTo)} style={backBtn}>{backLabel}</button>
      <div style={{ ...card, padding: '48px', textAlign: 'center', marginTop: '20px' }}>
        <div style={{ fontSize: '15px', color: C.text, fontFamily: 'Georgia, serif' }}>Provider not found</div>
      </div>
    </div>
  );

  const color = catColor(provider.category);

  return (
    <div style={{ padding: '28px 32px', minHeight: '100vh', background: C.surface, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <button onClick={() => navigate(backTo)} style={backBtn}>{backLabel}</button>

      {/* Header card */}
      <div style={{ ...card, padding: '24px 28px', marginTop: '16px', borderTop: provider.is_featured ? `3px solid ${C.gold}` : `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', gap: '18px', alignItems: 'flex-start' }}>
          <div style={{
            width: '64px', height: '64px', borderRadius: '4px', flexShrink: 0,
            background: logoUrl ? C.white : C.navy, border: `1px solid ${C.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
            fontFamily: 'Georgia, serif', fontSize: '26px', color: C.gold, fontWeight: 700,
          }}>
            {logoUrl ? <img src={logoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : provider.name.charAt(0)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px', flexWrap: 'wrap' }}>
              <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '23px', color: C.navy, fontWeight: 400, margin: 0, letterSpacing: '-0.3px' }}>{provider.name}</h1>
              <span style={{ background: `${color}14`, color, border: `1px solid ${color}33`, padding: '3px 9px', borderRadius: '2px', fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>{provider.category}</span>
              {isOwner && <span style={{ background: C.surface, color: C.muted, border: `1px solid ${C.border}`, padding: '3px 9px', borderRadius: '2px', fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Your Profile</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '12px', color: C.muted, flexWrap: 'wrap' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="1.8" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                {hqLabel(provider)}
              </span>
              {provider.website && (
                <a href={`https://${provider.website.replace(/^https?:\/\//, '')}`} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '4px', color: C.teal, textDecoration: 'none' }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={C.teal} strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                  {provider.website.replace(/^https?:\/\//, '')}
                </a>
              )}
              {provider.linkedin && (
                <a href={provider.linkedin.startsWith('http') ? provider.linkedin : `https://${provider.linkedin}`} target="_blank" rel="noopener noreferrer" style={{ color: C.teal, textDecoration: 'none' }}>LinkedIn</a>
              )}
            </div>
          </div>
          {!isOwner && (
            <button
              onClick={() => setTab('connect')}
              style={{
                background: conn?.status === 'approved' ? C.success : conn ? C.surface : C.navy,
                color: conn?.status === 'approved' ? C.white : conn ? C.amber : C.white,
                border: conn && conn.status !== 'approved' ? `1px solid ${C.amber}44` : 'none',
                borderRadius: '3px', padding: '9px 20px', fontSize: '12.5px', fontWeight: 600,
                cursor: 'pointer', fontFamily: 'system-ui', whiteSpace: 'nowrap',
              }}
            >{conn?.status === 'approved' ? 'Connected' : conn ? 'Request Pending' : 'Connect'}</button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '2px', marginTop: '20px', borderBottom: `1px solid ${C.border}` }}>
        {([['overview', 'Overview'], ['products', `Products & Pricing`], ['connect', 'Connect']] as [Tab, string][]).map(([key, lbl]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '10px 16px',
            fontSize: '13px', fontFamily: 'system-ui', fontWeight: tab === key ? 600 : 500,
            color: tab === key ? C.navy : C.muted,
            borderBottom: tab === key ? `2px solid ${C.navy}` : '2px solid transparent', marginBottom: '-1px',
          }}>{lbl}{key === 'products' && products.length > 0 ? ` (${products.length})` : ''}</button>
        ))}
      </div>

      <div style={{ marginTop: '20px' }}>
        {tab === 'overview' && (
          <OverviewTab provider={provider} deckUrl={deckUrl} videoUrl={videoUrl} logoUrl={logoUrl} />
        )}
        {tab === 'products' && <ProductsTab products={products} />}
        {tab === 'connect' && (
          <ConnectTab
            provider={provider} conn={conn} setConn={setConn}
            isOwner={isOwner} user={user} firm={firm}
          />
        )}
      </div>
    </div>
  );
}

const backBtn: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer', color: C.muted,
  fontSize: '12.5px', fontFamily: 'system-ui', padding: 0,
};

// ── Overview ─────────────────────────────────────────────────────────────────
function OverviewTab({ provider, deckUrl, videoUrl, logoUrl }: {
  provider: ServiceProvider; deckUrl: string | null; videoUrl: string | null; logoUrl: string | null;
}) {
  const card: React.CSSProperties = { background: C.white, border: `1px solid ${C.border}`, borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', alignItems: 'start' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ ...card, padding: '22px 24px' }}>
          <div style={{ ...labelStyle, marginBottom: '10px' }}>Overview</div>
          <p style={{ fontSize: '13.5px', color: C.textSec, lineHeight: 1.7, margin: 0, whiteSpace: 'pre-wrap' }}>
            {provider.description || 'No description provided yet.'}
          </p>
        </div>

        {videoUrl && (
          <div style={{ ...card, padding: '22px 24px' }}>
            <div style={{ ...labelStyle, marginBottom: '10px' }}>Demo Video</div>
            {/* No crossOrigin, setting it makes externally hosted videos fail CORS. */}
            <video
              src={videoUrl}
              controls
              poster={logoUrl || undefined}
              style={{
                width: '100%',
                height: '420px',
                borderRadius: '4px',
                background: C.black,
                display: 'block',
                objectFit: 'contain',
              }}
            />
          </div>
        )}

        {(provider.regions_covered || []).length > 0 && (
          <div style={{ ...card, padding: '22px 24px' }}>
            <div style={{ ...labelStyle, marginBottom: '10px' }}>Regions Covered</div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {(provider.regions_covered || []).map(r => (
                <span key={r} style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.textSec, fontSize: '11.5px', fontWeight: 500, padding: '4px 10px', borderRadius: '2px' }}>{r}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ ...card, padding: '22px 24px' }}>
          <div style={{ ...labelStyle, marginBottom: '12px' }}>At a Glance</div>
          {[
            ['Founded', provider.founded_year ? String(provider.founded_year) : '-'],
            ['Clients', provider.client_count != null ? String(provider.client_count) : '-'],
            ['Headquarters', hqLabel(provider)],
          ].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12.5px' }}>
              <span style={{ color: C.muted }}>{k}</span>
              <span style={{ color: C.text, fontWeight: 600 }}>{v}</span>
            </div>
          ))}
        </div>

        {deckUrl && (
          <a href={deckUrl} target="_blank" rel="noopener noreferrer" style={{
            ...card, padding: '16px 18px', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '12px',
          }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '4px', background: `${C.danger}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.danger} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
            </div>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: C.navy }}>Pitch Deck</div>
              <div style={{ fontSize: '11px', color: C.muted }}>Download PDF ↓</div>
            </div>
          </a>
        )}
      </div>
    </div>
  );
}

// ── Products ─────────────────────────────────────────────────────────────────
function ProductsTab({ products }: { products: ServiceProduct[] }) {
  const card: React.CSSProperties = { background: C.white, border: `1px solid ${C.border}`, borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
  if (!products.length) return (
    <div style={{ ...card, padding: '48px', textAlign: 'center' }}>
      <div style={{ fontSize: '14px', fontWeight: 600, color: C.text, marginBottom: '6px' }}>No products listed yet</div>
      <div style={{ fontSize: '13px', color: C.muted }}>This provider hasn't published their offerings.</div>
    </div>
  );
  const priceLabel = (p: ServiceProduct) => {
    const cur = p.price_currency || 'USD';
    const from = p.price_from ? `${cur} ${p.price_from}` : '';
    const to = p.price_to ? ` – ${p.price_to}` : '';
    return (from || to) ? `${from}${to}` : 'On request';
  };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
      {products.map(p => (
        <div key={p.id} style={{ ...card, padding: '18px 20px', borderLeft: p.is_flagship ? `3px solid ${C.gold}` : `1px solid ${C.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
            <div style={{ fontFamily: 'Georgia, serif', fontSize: '15px', color: C.navy }}>{p.name}</div>
            {p.is_flagship && <span style={{ background: `${C.gold}22`, color: C.amber, border: `1px solid ${C.gold}44`, fontSize: '8px', fontWeight: 700, padding: '1px 5px', borderRadius: '2px', letterSpacing: '0.5px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Flagship</span>}
          </div>
          {p.description && <p style={{ fontSize: '12.5px', color: C.textSec, lineHeight: 1.6, margin: '0 0 12px' }}>{p.description}</p>}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', paddingTop: '10px', borderTop: `1px solid ${C.borderLt}` }}>
            <span style={{ fontSize: '15px', fontWeight: 700, color: C.navy, fontFamily: 'Georgia, serif' }}>{priceLabel(p)}</span>
            {p.price_model && <span style={{ fontSize: '10.5px', color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{p.price_model}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Connect / Messaging ──────────────────────────────────────────────────────
function ConnectTab({ provider, conn, setConn, isOwner, user, firm }: {
  provider: ServiceProvider;
  conn: ServiceConnection | null;
  setConn: (c: ServiceConnection | null) => void;
  isOwner: boolean;
  user: { id: string } | null;
  firm: { id: string } | null;
}) {
  const card: React.CSSProperties = { background: C.white, border: `1px solid ${C.border}`, borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
  const [message, setMessage]   = useState('');
  const [sending, setSending]   = useState(false);
  const [error, setError]       = useState('');
  const [messages, setMessages] = useState<ServiceMessage[]>([]);
  const [newMsg, setNewMsg]     = useState('');
  const endRef = useRef<HTMLDivElement>(null);

  const approved = conn?.status === 'approved';

  useEffect(() => {
    if (!approved || !conn) return;
    fetchMessages(conn.id);
    const channel = supabase.channel(`sc-messages-${conn.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'service_messages', filter: `connection_id=eq.${conn.id}` },
        async payload => {
          const m = payload.new as ServiceMessage;
          setMessages(prev => prev.some(x => x.id === m.id) ? prev : [...prev, m]);
          if (m.sender_id !== user?.id) {
            await supabase.from('service_messages').update({ read_at: new Date().toISOString() }).eq('id', m.id);
          }
        })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
    // eslint-disable-next-line
  }, [approved, conn?.id]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  async function fetchMessages(cid: string) {
    const { data } = await supabase.from('service_messages').select('*').eq('connection_id', cid).order('created_at', { ascending: true });
    setMessages((data as ServiceMessage[]) || []);
    if (user) {
      await supabase.from('service_messages').update({ read_at: new Date().toISOString() })
        .eq('connection_id', cid).neq('sender_id', user.id).is('read_at', null);
    }
  }

  const requestConnection = async () => {
    if (!user) { setError('Please sign in.'); return; }
    if (!firm) { setError('You need a firm profile before connecting.'); return; }
    setSending(true); setError('');
    const { data, error } = await supabase.from('service_connections').insert({
      requester_firm_id: firm.id, provider_id: provider.id, requested_by: user.id, status: 'pending',
    }).select().single();
    if (error) { setError(error.message); setSending(false); return; }
    const newConn = data as ServiceConnection;
    setConn(newConn);
    // Optional opening message once the request exists (visible after approval)
    if (message.trim()) {
      await supabase.from('service_messages').insert({ connection_id: newConn.id, sender_id: user.id, content: message.trim() });
    }
    setSending(false);
  };

  const sendMessage = async () => {
    if (!newMsg.trim() || !conn || !user) return;
    const content = newMsg.trim(); setNewMsg('');
    const { data } = await supabase.from('service_messages').insert({ connection_id: conn.id, sender_id: user.id, content }).select().single();
    if (data) setMessages(prev => [...prev, data as ServiceMessage]);
  };

  const fmt = (ts: string) => new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  if (isOwner) return (
    <div style={{ ...card, padding: '32px', textAlign: 'center' }}>
      <div style={{ fontSize: '14px', color: C.text, fontFamily: 'Georgia, serif', marginBottom: '6px' }}>This is your public profile</div>
      <div style={{ fontSize: '12.5px', color: C.muted }}>Manage connections and messages from your provider dashboard.</div>
    </div>
  );

  // Approved → messaging
  if (approved && conn) return (
    <div style={{ ...card, display: 'flex', flexDirection: 'column', height: '520px', overflow: 'hidden' }}>
      <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: C.navy }}>Conversation with {provider.name}</div>
        <div style={{ fontSize: '11px', color: C.success, fontWeight: 600 }}>● Connected</div>
      </div>
      <div style={{ flex: 1, padding: '20px 24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px', background: C.surface }}>
        {messages.length === 0 && <div style={{ textAlign: 'center', color: C.muted, fontSize: '13px', marginTop: '40px' }}>No messages yet, say hello.</div>}
        {messages.map(m => {
          const isMe = m.sender_id === user?.id;
          return (
            <div key={m.id} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
              <div style={{ maxWidth: '60%', padding: '10px 13px', borderRadius: '4px', background: isMe ? C.navy : C.white, color: isMe ? C.white : C.text, fontSize: '13px', lineHeight: 1.55, border: isMe ? 'none' : `1px solid ${C.border}`, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                <div>{m.content}</div>
                <div style={{ fontSize: '10px', marginTop: '4px', opacity: 0.55, textAlign: 'right', fontFamily: '"Courier New", monospace' }}>{fmt(m.created_at)}</div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: '8px' }}>
        <input value={newMsg} onChange={e => setNewMsg(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
          placeholder="Type a message…" style={{ flex: 1, background: C.surface, border: `1px solid ${C.border}`, borderRadius: '3px', color: C.text, fontSize: '13px', padding: '9px 12px', outline: 'none', fontFamily: 'system-ui' }}
          onFocus={e => (e.target.style.borderColor = C.navy)} onBlur={e => (e.target.style.borderColor = C.border)} />
        <button onClick={sendMessage} style={{ background: C.navy, border: 'none', borderRadius: '3px', color: C.white, fontSize: '12.5px', fontWeight: 600, padding: '9px 18px', cursor: 'pointer', fontFamily: 'system-ui' }}>Send</button>
      </div>
    </div>
  );

  // Pending
  if (conn) return (
    <div style={{ ...card, padding: '40px', textAlign: 'center' }}>
      <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#FFFBEB', border: '1px solid #FDE68A', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.amber} strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
      </div>
      <div style={{ fontSize: '15px', color: C.text, fontFamily: 'Georgia, serif', marginBottom: '6px' }}>Connection request pending</div>
      <div style={{ fontSize: '12.5px', color: C.muted, lineHeight: 1.6, maxWidth: '380px', margin: '0 auto' }}>
        {provider.name} has received your request. You'll be able to message them here once they approve the connection.
      </div>
    </div>
  );

  // Not connected → request form
  return (
    <div style={{ ...card, padding: '28px 32px', maxWidth: '560px' }}>
      <div style={{ fontFamily: 'Georgia, serif', fontSize: '17px', color: C.navy, marginBottom: '4px' }}>Connect with {provider.name}</div>
      <div style={{ fontSize: '12.5px', color: C.muted, marginBottom: '20px', lineHeight: 1.6 }}>
        Send a connection request. Once approved, you can exchange messages, share documents and arrange an introduction.
      </div>
      {error && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '3px', padding: '8px 12px', marginBottom: '14px', fontSize: '12px', color: C.danger }}>{error}</div>}
      <label style={labelStyle}>Message (optional)</label>
      <textarea value={message} onChange={e => setMessage(e.target.value)} rows={4} placeholder={`Tell ${provider.name} what you're looking for…`}
        style={{ width: '100%', background: C.white, border: `1px solid ${C.border}`, borderRadius: '3px', color: C.text, fontSize: '13px', padding: '9px 11px', outline: 'none', fontFamily: 'system-ui', resize: 'vertical', lineHeight: 1.6, boxSizing: 'border-box', marginBottom: '16px' }}
        onFocus={e => (e.target.style.borderColor = C.navy)} onBlur={e => (e.target.style.borderColor = C.border)} />
      <button onClick={requestConnection} disabled={sending} style={{ background: C.navy, color: C.white, border: 'none', borderRadius: '3px', padding: '10px 22px', fontSize: '13px', fontWeight: 600, cursor: sending ? 'not-allowed' : 'pointer', fontFamily: 'system-ui', opacity: sending ? 0.7 : 1 }}>
        {sending ? 'Sending…' : 'Send Connection Request'}
      </button>
    </div>
  );
}
